/**
 * Access Code Entry Page
 *
 * Landing page for signer portal when accessed without a direct link.
 * Allows users to enter an access code to retrieve their document.
 */
import { useState } from 'react';
import { FileText, CheckCircle2, Shield, ArrowRight, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, Card, CardContent, Spinner } from '@respectly/ui';
import { executeRecaptcha } from '@respectly/utils';
import { PortalLayout } from '../layouts/PortalLayout';

export function AccessCode() {
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setError('Please enter an access code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Validate the code by trying to fetch the document
      const recaptchaToken = await executeRecaptcha('ACCESS_CODE');
      const response = await fetch(
        `/public/sign/${code}/validate?token=${code}&recaptchaToken=${encodeURIComponent(recaptchaToken)}`
      );
      const result = await response.json();

      if (!response.ok) {
        setError('Invalid access code. Please check and try again.');
        setIsLoading(false);
        return;
      }

      if (result.data?.status === 'valid') {
        // Redirect to the signing page
        window.location.href = `/sign/${code}?token=${code}`;
      } else if (result.data?.status === 'expired') {
        setError('This access code has expired.');
        setIsLoading(false);
      } else if (result.data?.status === 'already_completed') {
        setError('This document has already been signed.');
        setIsLoading(false);
      } else {
        setError('Invalid access code. Please check and try again.');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Error validating code:', err);
      setError('Unable to verify code. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <PortalLayout>
      <div className="flex flex-1 items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-md"
        >
          <Card>
            <CardContent className="p-6 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <FileText className="h-8 w-8 text-primary-600" />
              </div>
              <h1 className="text-xl font-semibold">Sign Your Document</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                You've been sent a document to sign. Enter the code from your
                email or SMS to continue.
              </p>

              {/* Access Code Input */}
              <form onSubmit={handleSubmit} className="mt-6">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.toUpperCase());
                    setError(null);
                  }}
                  placeholder="Enter access code"
                  className={`w-full rounded-lg border bg-background px-4 py-3 text-center text-lg tracking-widest focus:outline-none focus:ring-2 focus:ring-ring ${
                    error ? 'border-red-500' : 'border-input'
                  }`}
                  maxLength={36}
                  autoComplete="off"
                  autoFocus
                />

                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 flex items-center justify-center gap-1 text-sm text-red-600"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </motion.p>
                )}

                <Button
                  type="submit"
                  className="mt-4 w-full"
                  size="lg"
                  disabled={isLoading || !code.trim()}
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              {/* Security Badge */}
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>Secure & encrypted</span>
              </div>
            </CardContent>
          </Card>

          {/* Features */}
          <div className="mt-8 grid grid-cols-3 gap-4 text-center text-sm">
            <div>
              <CheckCircle2 className="mx-auto h-5 w-5 text-success" />
              <p className="mt-1 text-muted-foreground">Legally binding</p>
            </div>
            <div>
              <CheckCircle2 className="mx-auto h-5 w-5 text-success" />
              <p className="mt-1 text-muted-foreground">Mobile friendly</p>
            </div>
            <div>
              <CheckCircle2 className="mx-auto h-5 w-5 text-success" />
              <p className="mt-1 text-muted-foreground">Audit trail</p>
            </div>
          </div>
        </motion.div>
      </div>
    </PortalLayout>
  );
}
